'use strict';

const isObject = require('lodash.isobject');
const stringify = require('json-stringify-safe');

/**
 * @typedef {Object} ErrorLog
 * @private
 * @property {string} userAgent - User agent retrieved from window.navigator
 * @property {string} message - Error message
 * @property {string} url - File in which the error occurred
 * @property {number} columnNumber - Column on which the error occurred
 * @property {number} lineNumber - Line on which the error occurred
 * @property {string} stack - Stack trace, if available
 */

/**
 * @typedef {Object} ClientLog
 * @private
 * @property {string} userAgent - User agent retrieved from window.navigator
 * @property {string} message - Developer authored message
 * @property {string} url - route from which the error was thrown
 * @property {string} type - What kind of log is this? info, error.
 */

/**
 * @name
 * createClientLog
 * @private
 * @description
 * A function for creating client logs.
 * @param {string[]} Array containing one string, the message to post.
 * @param {string} logType - What kind of log is this? ex: info, error.
 * @return {ClientLog}
 */

function createClientLog([message], logType) {
    return {
        type: logType,
        userAgent: navigator.userAgent,
        message,
        url: window.location.href
    };
}

/**
 * @name
 * createErrorLog
 * @private
 * @description
 * Formats data passed to log handlers into a consistently formatted
 * log. Limit stack traces to 10 entries.
 * @param {string|Error} messageOrEvent
 * @param {url=} source
 * @param {number=} lineno
 * @param {number=} colno
 * @param {Error=} errorObj
 * @return {ErrorLog}
 */

function createErrorLog([messageOrEvent, url, lineNumber, columnNumber, errorObj]) {
    return {
        userAgent: navigator.userAgent,
        message: isObject(messageOrEvent) ? stringify(messageOrEvent) : messageOrEvent,
        url: url || '',
        lineNumber,
        columnNumber: typeof columnNumber === 'number' ?
            columnNumber :
            window.event && window.event.errorCharacter,
        stack: errorObj && errorObj.stack && errorObj.stack.split(/\r\n|\r|\n/)
            .slice(0, 10)
            .join('\r\n')
    };
}

/**
 * @name createLog
 * @private
 * @description
 * Determines if the log information came from window.onerror or was created
 * by the developer and calls either #createErrorLog or #createClientLog.
 * @param {array} logInfo - Log data from window.onerror or the developer.
 * @param {string} logType - What kind of log is this?
 * @returns {ErrorLog|ClientLog}
 */

function createLog(logInfo, logType) {
    if (logType === 'exception') {
        return createErrorLog(logInfo, logType);
    }
    return createClientLog(logInfo, logType);
}

module.exports = createLog;